// Admin dashboard: reports table, stats, and every record action.
import { createSlice } from '@reduxjs/toolkit';
import { ENDPOINTS } from '../constants/apiEndpoints';
import { apiFetch } from '../lib/api';
import { createApiThunk } from './createApiThunk';

/* ── list + stats (stored in the slice) ── */

// params: { page, limit, status, search, order }
export const fetchReports = createApiThunk('dashboard/fetchReports', (params, { signal }) => {
  const qs = new URLSearchParams({
    page: params.page,
    limit: params.limit,
    status: params.status,
    search: params.search,
    sortBy: 'createdAt',
    order: params.order,
  });
  return apiFetch(`${ENDPOINTS.DASHBOARD}?${qs}`, { signal });
});

export const fetchStats = createApiThunk('dashboard/fetchStats', async (_arg, { signal }) => {
  const data = await apiFetch(ENDPOINTS.DASHBOARD_STATS, { signal });
  if (!data?.success) throw new Error(data?.message || 'Failed to load stats');
  return data.stats;
});

// Reloads the current table page and the stats, e.g. after a record changes.
export const refreshDashboard = () => (dispatch, getState) => {
  const { lastQuery } = getState().dashboard;
  return Promise.all([lastQuery && dispatch(fetchReports(lastQuery)), dispatch(fetchStats())]);
};

/* ── record actions (results go back to the caller) ── */

export const fetchRecordDetails = createApiThunk('dashboard/fetchRecordDetails', (token, { signal }) =>
  apiFetch(ENDPOINTS.DASHBOARD_RECORD(token), { signal }),
);

export const deleteVerification = createApiThunk('dashboard/deleteVerification', ({ token, reason }) =>
  apiFetch(ENDPOINTS.DELETE_VERIFICATION(token), { method: 'DELETE', body: { reason } }),
);

export const invalidateLink = createApiThunk('dashboard/invalidateLink', ({ token, reason }) =>
  apiFetch(ENDPOINTS.INVALIDATE_LINK(token), { method: 'POST', body: { reason } }),
);

export const publishRentsecure = createApiThunk('dashboard/publishRentsecure', async (token) => {
  const data = await apiFetch(ENDPOINTS.PUBLISH_RENTSECURE(token), { method: 'POST' });
  if (data?.success === false) throw new Error(data.message || data.error || 'Publish failed');
  return data;
});

export const resendLink = createApiThunk('dashboard/resendLink', ({ candidateId, candidateComponentId }) =>
  apiFetch(ENDPOINTS.RESEND_LINK, { method: 'POST', body: { candidateId, candidateComponentId } }),
);

export const markDownloaded = createApiThunk('dashboard/markDownloaded', ({ token, downloadedBy, downloadedByName }) =>
  apiFetch(ENDPOINTS.MARK_DOWNLOADED(token), { method: 'PUT', body: { downloadedBy, downloadedByName } }),
);

// formData: FormData with the edited fields and any replaced files.
export const updateVerification = createApiThunk('dashboard/updateVerification', ({ token, formData }) =>
  apiFetch(ENDPOINTS.UPDATE_VERIFICATION(token), { method: 'PUT', body: formData }),
);

export const updateGeo = createApiThunk('dashboard/updateGeo', ({ token, ...geo }) =>
  apiFetch(ENDPOINTS.UPDATE_GEO(token), { method: 'PUT', body: geo }),
);

export const convertPdf = createApiThunk('dashboard/convertPdf', (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiFetch(ENDPOINTS.CONVERT_PDF, { method: 'POST', body: formData });
});

export const exportReports = createApiThunk('dashboard/exportReports', () =>
  apiFetch(ENDPOINTS.DASHBOARD_EXPORT),
);

/* ── slice ── */

const initialState = {
  records: [],
  totalPages: 1,
  totalRecords: 0,
  reportsStatus: 'idle', // idle | loading | succeeded | failed
  reportsLoaded: false, // true once any page has arrived
  reportsRequestId: null,
  lastQuery: null,
  stats: { total: 0, clear: 0, pending: 0 },
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    dashboardReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state, { meta }) => {
        state.reportsStatus = 'loading';
        state.reportsRequestId = meta.requestId;
        state.lastQuery = meta.arg;
      })
      // Only the latest request may update the table, so a slow earlier page
      // can't overwrite a newer one.
      .addCase(fetchReports.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.reportsRequestId) return;
        state.reportsStatus = 'succeeded';
        state.reportsLoaded = true;
        state.records = payload?.records || [];
        state.totalPages = payload?.pagination?.totalPages || 1;
        state.totalRecords = payload?.pagination?.totalRecords || 0;
      })
      .addCase(fetchReports.rejected, (state, { meta }) => {
        if (meta.requestId !== state.reportsRequestId || meta.aborted) return;
        state.reportsStatus = 'failed';
        state.records = [];
      })
      .addCase(fetchStats.fulfilled, (state, { payload }) => {
        state.stats = payload;
      });
  },
});

export const { dashboardReset } = dashboardSlice.actions;
export default dashboardSlice.reducer;
